import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

//MUI
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Popover from "@mui/material/Popover";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";

//MUI Icons
import DoneAllIcon from "@mui/icons-material/DoneAll";
import RemoveDoneIcon from "@mui/icons-material/RemoveDone";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import AdresaIcon from "@mui/icons-material/Home";
import KatastarIcon from "@mui/icons-material/Grid3x3";
import KatoviIcon from "@mui/icons-material/Segment";
import DijeloviIcon from "@mui/icons-material/ViewCompact";
import AreaIcon from "@mui/icons-material/Crop32";

//Custom
import SnackbarContext from "@/ui/SnackbarContext/SnackbarContext";
import ToolbarFillContent from "@/ui/Toolbar/ToolbarFillContent";
import modelObjekti from "@/models/objekti";
import ObjectPicker from "./ObjectPicker";
import ToggleTableButton from "./ToggleTableButton";
import ObjectAnalysisTable from "./ObjectAnalysisTable";
import objektiService from "@/services/objektiService";

//Types
import { DCRecord } from "@/@types/lib/dataController";
import { PickerItem } from "@/@types/controls/controls";
import { IResolve } from "@/lib/ActPromise";
import UserContext from "@/components/UserContext/UserContext";
import { sifrarnici } from "@/lib/sifrarnici";
import { Permissions } from "@/lib/permissions";

export type ObjectAnalysisInfoStripProps = {
  records: Array<DCRecord>;
  selectedRecord: DCRecord | null;
  onRecordSelect: (id: number, record: DCRecord | null) => void;
  onRecordChange: () => void;
  isDisabled: boolean;
  checkIfDioChanged: (shouldReturnNull?: boolean) => Promise<boolean>|IResolve<boolean>;
  saveEditingDio: (refreshDijelovi?: boolean) => Promise<any>;
  checkIfNapomenaChanged: (shouldReturnNull?: boolean) => Promise<boolean>;
  saveNapomena: () => Promise<any>;
  openSwalDioNapomena: (canContinueDio: boolean, canContinueNap: boolean) => void;
}

const ObjectAnalysisToolbar = (props: ObjectAnalysisInfoStripProps) => {

  const snackbarContext = useContext(SnackbarContext);
  const userContext = useContext(UserContext);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [tableOpen, setTableOpen] = useState(false);
  const [originalRecord, setOriginalRecord] = useState<DCRecord | null>(null);
  const [events, setEvents] = useState<boolean>(false);

  const { t } = useTranslation();

  const { records, selectedRecord, onRecordSelect, onRecordChange, isDisabled } = props;
  const { saveEditingDio, saveNapomena, checkIfDioChanged, checkIfNapomenaChanged, openSwalDioNapomena } = props;

  const items = records.map(x => { return { label: x.id?.toString(), value: x.id } as PickerItem });
  const selectedItem = selectedRecord ? { label: selectedRecord.id?.toString(), value: selectedRecord.id } as PickerItem : null;

  useEffect(() => {
    if (selectedRecord) {
      if (events) {
        unregisterEvents();
      }
      registerEvents();
      setEvents(true);
      return () => unregisterEvents()
    }
    return () => {};
  }, [selectedRecord, isDisabled])

  const eventRegistrationFn = (evt: KeyboardEvent) => {
    if (evt.altKey && evt.shiftKey) {
      switch (evt.code) {
        case 'KeyE': handleToggleDone(); break;
      }
    }
  }

  const registerEvents = () => {
    window.addEventListener('keydown', eventRegistrationFn);
  }

  const unregisterEvents = () => {
    window.removeEventListener('keydown', eventRegistrationFn)
  };


  useEffect(() => {
    if (selectedRecord) {
      if (originalRecord === null || originalRecord.id !== selectedRecord.id) {
        setOriginalRecord({ ...selectedRecord });
      }
    } else {
      setOriginalRecord(null);
    }
  }, [selectedRecord]);  

  const hanleRecordSelect = (id: number | null) => {
    if (id) {
      const rec = records.find(x => x.id === id);
      if (rec) {
        onRecordSelect(id, rec);
      } else {
        onRecordSelect(-1, null);
      }
    } else {
      onRecordSelect(-1, null);
    }
  }

  const handleToggleTableOpen = (evt: React.MouseEvent<HTMLButtonElement>, newValue: boolean) => {
    setTableOpen(newValue);

    if (newValue === true) {
      setAnchorEl(evt.currentTarget);
    } else {
      setAnchorEl(null);
    }
  }

  const handleCloseTable = () => {
    setTableOpen(false);
    setAnchorEl(null);
  }

  const handleToggleGlavniObjekt = () => {

    if (selectedRecord && selectedRecord.id) {
      const currentGlavniObjekt = selectedRecord.gl_obj;
      const newValue = !currentGlavniObjekt;

      objektiService.partiallyUpdateObjekt(selectedRecord.id as number, { gl_obj: newValue })
        .then(resp => {
          if (resp.success) {
            onRecordChange();
            snackbarContext.showNotification("update", "success");
          }
        })
    }
  }

  const handleToggleDoneWrapped = () => {
    const newValue = !isDone;

    if (selectedRecord && selectedRecord.id) {

      objektiService.partiallyUpdateObjekt(selectedRecord.id as number, { pregledano: newValue })
        .then((resp) => {
          if (resp.success) {
            onRecordChange();
            snackbarContext.showNotification("update", "success");
          }
        })
        .catch(err => {
          /*
            err = {
              error: {
                detail: {errcode: 999, errmsg: 'ERROR: relation "op3.objekti_xx" does not exist\nLI…dano = false WHERE objekti_i...'}
                message: "ERROR: relation \"op3.objekti_xx\" does not exist\nLINE 1: UPDATE op3.objekti_xx SET pregledano = false WHERE objekti_i..."
              }
              success: false
            }
          */
          snackbarContext.showNotification(`${t("objekti.fail_set_pregledano")}\n${err.error.message}`.replaceAll(':', ' - '), "error");
        })
    }
  }

  const handleToggleDone = () => {
    checkIfDioChanged(false).then(canContinueDio => {
      checkIfNapomenaChanged(false).then(canContinueNap => {
        if (canContinueDio && canContinueNap) {
          handleToggleDoneWrapped();
        } else {
          openSwalDioNapomena(canContinueDio, canContinueNap);
        }
      })
    })
  }

  const checkChange = (fieldId: string) => {
    if (selectedRecord && originalRecord) {
      if ((selectedRecord[fieldId] as number) < (originalRecord[fieldId] as number)) {
        return { color: "red", fontWeight: "bold" };
      } else if ((selectedRecord[fieldId] as number) > (originalRecord[fieldId] as number)) {
        return { color: "#32CD32", fontWeight: "bold" };
      } else {
        // equal
        return {};
      }
    } else {
      return {};
    }
  }

  const getTTip = (ttoken: string, fieldId: string) => {
    if (selectedRecord && originalRecord) {
      if ((selectedRecord[fieldId] as number) !== (originalRecord[fieldId] as number)) {
        return `${t(ttoken)} (${t("common.prev_value")}: ${originalRecord[fieldId]})`;
      } else {
        // equal
        return t(ttoken) as string;
      }
    } else {
      return t(ttoken) as string;
    }
  }

  const hasRecords = records.length > 0 ? true : false;
  const hasSelectedRecord = selectedRecord ? true : false;
  const isDone = selectedRecord && selectedRecord.pregledano === true ? true : false;

  const isAdmin = userContext?.hasAnyPermission([Permissions.Administration])
  const isPassedQC = selectedRecord ? !!selectedRecord.qc_check : false;

  const isGlavniObjekt = selectedRecord && selectedRecord.gl_obj ? true : false;

  return (
    <Toolbar variant="dense" sx={{ borderSize: 2, borderStyle: "solid", borderColor: "primary.light", mb: 1 }}>
      <Typography>{t("objekti.picker_label")}</Typography>
      <ObjectPicker items={items} selectedItem={selectedItem} onChange={hanleRecordSelect} />
      {selectedRecord ?
        <>
          <Chip
            color="primary"
            variant={isGlavniObjekt ? "filled" : "outlined"}
            label={t("buttons.gl_obj")}
            icon={isGlavniObjekt ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
            sx={{ mx: 1 }}
            clickable={!isDisabled}
            onClick={isDisabled ? () => {} : handleToggleGlavniObjekt}
          />
          <Tooltip title={t("objekti.adresa_objekta") as string}>
            <Chip
              label={selectedRecord.adresa_full}
              icon={<AdresaIcon />}
              sx={{ mx: 1 }}
            />
          </Tooltip>
          <Tooltip title={t("komunalne_obveze.kc") as string}>
            <Chip
              label={`${selectedRecord.kc} (${selectedRecord.ko_ime})`}
              icon={<KatastarIcon />}
              sx={{ mx: 1 }}
            />
          </Tooltip>
          <Tooltip title={getTTip('objekti.broj_katova', 'broj_katova')}>
            <Chip
              id="op3-chip-broj-katova"
              //@ts-ignore
              label={OP3_CALC.broj_katova || '0'}
              icon={<KatoviIcon style={{ transform: 'rotate(180deg)' }} />}
              sx={{ mx: 1, ...checkChange('broj_katova') }}
            />
          </Tooltip>
          <Tooltip title={getTTip('objekti.broj_dijelova', 'broj_dijelova')}>
            <Chip
              id="op3-chip-broj-dijelova"
              //@ts-ignore
              label={OP3_CALC.broj_dijelova || '0'}
              icon={<DijeloviIcon />}
              sx={{ mx: 1, ...checkChange('broj_dijelova') }}
            />
          </Tooltip>
          <Tooltip title={getTTip('objekti.pre_pb', 'pre_pb')}>
            <Chip
              id="op3-chip-pre-pb"
              //@ts-ignore
              label={OP3_CALC.pre_pb || '0'}
              icon={<AreaIcon />}
              sx={{ mx: 1, ...checkChange('pre_pb') }}
            />
          </Tooltip>
          <Tooltip title={t("objekti.pre_pt") as string}>
            <Chip
              label={"PRE PT:" + (selectedRecord.pre_pt ? Math.round(selectedRecord.pre_pt as number).toString() : "")}
              icon={<AreaIcon />}
              sx={{ mx: 1 }}
            />
          </Tooltip>
        </> : null}

      <ToolbarFillContent />
      <Tooltip title={isDone || !hasSelectedRecord ? "" : t("tooltips.shortcuts.set_pregledano") as string}>
        <Chip
          color="primary"
          variant={isDone ? "filled" : "outlined"}
          label={t("buttons.pregledano")}
          icon={isDone ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />}
          sx={{ mx: 1 }}
          clickable={true}
          onClick={handleToggleDone}
          disabled={!hasSelectedRecord || (!isAdmin && isPassedQC)}
        />
      </Tooltip>
      {/*      <Button
        variant={isDone ? "outlined" : "contained"}
        startIcon={isDone ? <RemoveDoneIcon /> : <DoneAllIcon />}
        sx={{ mx: 1 }}
        disabled={!hasSelectedRecord}
        onClick={handleToggleDone}
      >
        {isDone ? t("buttons.remove_done") : t("buttons.mark_done")}
      </Button>*/}
      <ToggleTableButton open={tableOpen} onClick={handleToggleTableOpen} disabled={!hasRecords} />
      <Popover
        id="table_popover"
        open={true}
        anchorEl={anchorEl}
        onClose={handleCloseTable}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        style={{ visibility: tableOpen ? 'visible' : 'hidden' }}
      >
        <ObjectAnalysisTable
          records={records}
          onRowSelect={hanleRecordSelect}


          model={modelObjekti}
          titleToken={"titles.objekti"}
        />
      </Popover>
    </Toolbar>
  )

}

export default ObjectAnalysisToolbar;
